import type { CSSProperties } from "react";
import { stageLabel, stageVar } from "../lib/format";

/** Stage badge: tint and text derived from the stage colour (SPEC §14). */
export function StageBadge({ stage, lens = "req", plain }: { stage: string; lens?: "req" | "po"; plain?: boolean }) {
  if (!stage) return null;
  // In lists the stage reads as plain text, as Sertica's Status column does; the dot keeps its colour.
  if (plain)
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap" style={{ "--c": stageVar(stage, lens) } as CSSProperties}>
        <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "var(--c)" }} />
        {stageLabel(stage, lens)}
      </span>
    );
  return (
    <span className="badge" style={{ "--c": stageVar(stage, lens) } as CSSProperties}>
      <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "var(--c)" }} />
      {stageLabel(stage, lens)}
    </span>
  );
}

const FLAGS: [key: string, label: string, cssVar: string][] = [
  ["openForChanges", "Open for changes", "--flag-open_for_changes"],
  ["critical", "Critical item", "--flag-critical"],
  ["anomaly", "Anomaly", "--flag-anomaly"],
  ["urgent", "Urgent", "--flag-urgent"],
  ["rejectedOnReceipt", "Rejected on receipt", "--flag-rejected_on_receipt"],
  // Only present for users with budget access (SPEC §3.1).
  ["overBudget", "Over budget", "--flag-over_budget"],
];

/** Flags render as outlined chips, never filled (SPEC §3.1). */
export function Flags({ row }: { row: Record<string, unknown> }) {
  const on = FLAGS.filter(([k]) => row[k]);
  if (!on.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {on.map(([k, label, v]) => (
        <span key={k} className="flag" style={{ "--c": `var(${v})` } as CSSProperties}>
          {label}
        </span>
      ))}
    </span>
  );
}

/** Marks a line that orders a critical item (SPEC §9), with Sertica's remark on hover. */
/** A line's own Sertica marks, outlined as flags are: urgent, and on an RFQ still open. */
export function LineChip({ kind }: { kind: "urgent" | "open-rfq" }) {
  const [label, color, title] =
    kind === "urgent" ? ["Urgent", "var(--flag-urgent)", "This line is urgent in Sertica"] : ["On open RFQ", "var(--brand-2)", "This line is on an RFQ that is still open"];
  return (
    <span className="flag shrink-0" style={{ "--c": color } as CSSProperties} title={title}>
      {label}
    </span>
  );
}

export function CriticalChip({ remark }: { remark: string | null }) {
  return (
    <span className="flag shrink-0" style={{ "--c": "var(--flag-critical)" } as CSSProperties} title={remark ? `Critical item: ${remark}` : "Critical item in Sertica"}>
      Critical
    </span>
  );
}
